import { AxiosInstance } from "axios"

export type MatchStatus = "paired" | "source_a_only" | "source_b_only"

export interface PositionDiff {
  match_status: MatchStatus
  identifier: string
  description: string | null
  currency: string | null
  asset_type: string | null
  quantity_a: number | null
  quantity_b: number | null
  quantity_diff: number | null
  price_a: number | null
  price_b: number | null
  price_diff: number | null
  market_value_a: number | null
  market_value_b: number | null
  market_value_diff: number | null
  market_value_local_a: number | null
  market_value_local_b: number | null
  market_value_local_diff: number | null
  implied_fx_a: number | null
  implied_fx_b: number | null
  // Each side's unit price in fund currency, market value / quantity: the
  // figure the price-gap control reads (#1755), the quoting convention, the
  // FX and the multiplier all inside it. Null on a one-sided row, without a
  // market value or on a zero quantity. A price edit recomputes the Scient
  // one through the row's valuation (applyOverride).
  unit_price_a: number | null
  unit_price_b: number | null
  // Valuation factors: client-side MV recompute uses the single formula
  // qty × price × multiplier (× fx_rate for the fund-ccy value). Null when
  // unknown — an edit then records an override without recomputing MV.
  multiplication_factor: number | null
  fx_rate: number | null
  // The price-gap control for this row (CONTEXT.md, "Reconciliation
  // controls"), decided by the server against the fund's thresholds and the
  // CACEIS total: the relative gap against the CACEIS unit price in fund
  // currency (%), the gap's value in fund currency, and the status — null
  // when the row is outside the control (not a security, or priced on one
  // side only). The UI re-evaluates a row by the same formula only after a
  // price edit.
  price_gap_pct: number | null
  price_gap_value: number | null
  price_gap_status: ControlStatus | null
}

export interface ShareClassNavResult {
  code: string
  split_pct: number
  fee_amount: number
  nombre_parts: number
  nav_per_share_a: number | null
  nav_per_share_b: number | null
  // null when CACEIS publishes no per-share VL (feedless single-class funds)
  vl_parts_caceis: number | null
  diff_vs_caceis: number | null
}

export interface ShareClassFileMetadata {
  code: string
  an_parts: number
  nombre_parts: number
  vl_parts: number
}

// --- Reconciliation controls (CONTEXT.md, "Reconciliation controls") ---

// The fund's thresholds as applied to this compare (shown on screen so a
// later reading knows which rule ran that day).
export interface ControlThresholds {
  nav_threshold_bp: number
  price_gap_threshold_pct: number
  nav_share_threshold_pct: number
}

// "unverified": the control could not be evaluated (no Scient NAV, zero
// reference, an unknown valuation factor) — reported like a breach, never
// as a pass.
export type ControlStatus = "passed" | "breached" | "unverified"

// Which CACEIS figure the Scient NAV is paired with: the published VL on fed
// funds, the CACEIS-side NAV on feedless funds.
export type NavReference = "published_vl" | "caceis_nav"

export interface NavControlResult {
  code: string
  status: ControlStatus
  reference: NavReference
  nav_per_share_scient: number | null
  nav_per_share_reference: number | null
  gap_bp: number | null
  threshold_bp: number
}

// A share-count acceptance as the submission record carries it (CONTEXT.md,
// "Share-count acceptance"): who took the file's count as the known count,
// when, and why. Null on a compare — nothing has been accepted yet.
export interface ShareCountAcceptanceRecord {
  by: string
  at: string
  comment: string
}

// The share-count control for one share class: today's file count against
// the known count (the class's latest snapshot strictly before the NAV
// date). No threshold: passed when equal, breached otherwise, unverified
// when a fed class has no file count (not passed, nothing to accept). A
// breach refuses the submission until accepted; the UI never re-evaluates
// the file-vs-known reading — both counts are the server's facts.
export interface ShareCountControlResult {
  code: string
  status: ControlStatus
  // null on the first day of a fed fund (no prior snapshot).
  known_nombre_parts: number | null
  // null when unverified: a fed class the file carries no count for.
  file_nombre_parts: number | null
  // The operator's shares edit the server judged, when the submission
  // carried one; always null on a compare.
  edited_nombre_parts: number | null
  acceptance: ShareCountAcceptanceRecord | null
}

// The price-gap control for one row, as the report lists it (one per
// compare row, in row order); the row itself carries the same three facts,
// and the four prices — the unit prices in fund currency the gap was read
// on and the quotes beside them — so a breach reads on its own in the
// submission record.
export interface PriceGapControlResult {
  identifier: string
  status: ControlStatus | null
  unit_price_scient: number | null
  unit_price_caceis: number | null
  quoted_price_scient: number | null
  quoted_price_caceis: number | null
  gap_pct: number | null
  gap_value: number | null
  // Why the control could not be evaluated (a unit price, the CACEIS
  // quantity or the NAV reference missing); null otherwise.
  reason: string | null
}

// Every control the server decided for this compare, with the thresholds it
// applied. The UI renders these as given, and re-evaluates the NAV control
// for a class whose figures an edit changed and the price-gap control for a
// row whose price an edit changed, by the same rules (a control reads the
// corrected state); the server re-decides at submit.
export interface ControlReport {
  thresholds: ControlThresholds
  nav_controls: NavControlResult[]
  share_count_controls: ShareCountControlResult[]
  price_gap_controls: PriceGapControlResult[]
}

export interface ReconciliationResult {
  source_a_name: string
  source_b_name: string
  nav_date: string
  diffs: PositionDiff[]
  total_market_value_a: number
  total_market_value_b: number
  total_market_value_diff: number
  count_paired: number
  count_a_only: number
  count_b_only: number
  account_id: number | null
  share_classes: ShareClassNavResult[] | null
  share_class_metadata: ShareClassFileMetadata[] | null
  share_class_warning: string | null
  // null when the fund is not mapped to an account — nothing to evaluate.
  controls: ControlReport | null
}

// --- NAV Submission types (v0 — write-only) ---

export interface NavSubmissionRowSide {
  price?: string | null
  quantity?: string | null
  market_value?: string | null
}

export interface NavSubmissionRowOverrides {
  price?: string | null
  quantity?: string | null
  // Fee rows are corrected by amount — direct market value (issue #1630).
  market_value?: string | null
}

export interface NavSubmissionRow {
  identifier: string
  description?: string | null
  currency?: string | null
  asset_type?: string | null
  status: MatchStatus
  scient?: NavSubmissionRowSide | null
  caceis?: NavSubmissionRowSide | null
  diff?: NavSubmissionRowSide | null
  original_scient?: NavSubmissionRowSide | null
  overrides?: NavSubmissionRowOverrides | null
  comment?: string
  // The valuation factors of the compare row, echoed for the record, and
  // each side's unit price in fund currency as the operator saw it (the
  // Scient one recomputed through the row's valuation when corrected) so
  // the server re-decides the price-gap control on the same figures
  // (CONTEXT.md, "Control": a control reads the corrected state) — with
  // the quotes in `scient` and `caceis`. No price-gap flag travels: the
  // server decides.
  multiplication_factor?: string | null
  fx_rate?: string | null
  unit_price_a?: string | null
  unit_price_b?: string | null
}

// One edited shares outstanding per share class (issue #1631). The original
// is the pre-edit value — file figure for fed funds, carried-forward value
// for feedless ones.
export interface ShareClassOverride {
  code: string
  nombre_parts: string
  original_nombre_parts?: string | null
  comment?: string
}

// A share-count acceptance for one class (CONTEXT.md): the operator takes
// the file's count as the known count. The counts themselves are the
// server's; only the class and the optional comment travel.
export interface ShareCountAcceptance {
  code: string
  comment?: string
}

export interface ShareClassNavEntry {
  code: string
  nav_per_share_scient?: number | null
  nav_per_share_caceis?: number | null
}

export interface NavSubmissionCreateInput {
  account_id: number
  nav_date: string
  source_file: string
  source_type: string
  rows: NavSubmissionRow[]
  share_class_metadata?: ShareClassFileMetadata[] | null
  share_class_navs?: ShareClassNavEntry[] | null
  share_class_overrides?: ShareClassOverride[] | null
  // Acknowledgement (CONTEXT.md): the operator confirmed seeing the breaches
  // the controls reported. The server re-decides the controls from the
  // figures and records the acknowledgement only when it finds breaches.
  controls_acknowledged?: boolean
  // Share-count acceptances (CONTEXT.md): one per class whose file count
  // the operator accepts. A share-count breach on a class absent from this
  // list refuses the submission (409).
  share_count_acceptances?: ShareCountAcceptance[] | null
  // The NAV reference the price-gap control ran with at compare (the
  // CACEIS total of the compare), echoed so the server decides the gap
  // value's share of the NAV against the same figure; absent, it uses the
  // submitted rows' CACEIS total.
  nav_reference?: number | null
}

export interface NavSubmissionCreateResponse {
  s3_key: string
}

// --- Correction audit log (#1629) ---

export interface CorrectionEntry {
  id: number
  nav_date: string
  target: string
  field: "price" | "quantity" | "market_value" | "nombre_parts"
  old_value: string | null
  new_value: string
  author: string
  created_at: string
  comment: string
}

export interface CorrectionListResponse {
  corrections: CorrectionEntry[]
}

export interface IReconciliationApi {
  compare: (file: File, sourceType?: string) => Promise<ReconciliationResult>
  createSubmission: (
    input: NavSubmissionCreateInput,
  ) => Promise<NavSubmissionCreateResponse>
  listCorrections: (
    accountId: number,
    navDate: string,
  ) => Promise<CorrectionListResponse>
}

/**
 * Api routes for /api/v1/reconciliation/
 */
const reconciliationApi = (axiosClient: AxiosInstance): IReconciliationApi => ({
  /**
   * POST /api/v1/reconciliation/compare/
   */
  compare: async (file: File, sourceType = "caceis") => {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("source_type", sourceType)

    const { data } = await axiosClient.post<ReconciliationResult>(
      "/reconciliation/compare/",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    )
    return data
  },

  /**
   * POST /api/v1/reconciliation/submissions/
   */
  createSubmission: async (input: NavSubmissionCreateInput) => {
    const { data } = await axiosClient.post<NavSubmissionCreateResponse>(
      "/reconciliation/submissions/",
      input,
    )
    return data
  },

  /**
   * GET /api/v1/reconciliation/corrections/
   */
  listCorrections: async (accountId: number, navDate: string) => {
    const { data } = await axiosClient.get<CorrectionListResponse>(
      "/reconciliation/corrections/",
      { params: { account_id: accountId, nav_date: navDate } },
    )
    return data
  },
})

export default reconciliationApi
